import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Card } from "@/components/ui/card";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCountryDetection } from "@/hooks/useCountryDetection";

const ChildProfile = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [childGender, setChildGender] = useState("");
  const { country, isLoading: detectingLocation } = useCountryDetection();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!childName || !childAge || !childGender) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("Not authenticated");
        navigate("/auth");
        return;
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          child_name: childName,
          child_age: parseInt(childAge),
          child_gender: childGender,
          country: country,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast.success("Profile saved!");
      navigate("/home");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-6 md:p-8">
        <div className="text-center mb-6 md:mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-primary rounded-2xl mb-4">
            <Sparkles className="w-7 h-7 md:w-8 md:h-8 text-primary-foreground" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Tell us about your child</h1>
          <p className="text-sm md:text-base text-muted-foreground">This helps us create personalized stories</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5 md:space-y-6">
          <div className="space-y-2">
            <Label htmlFor="childName">Child's Name</Label>
            <Input
              id="childName"
              type="text"
              value={childName}
              onChange={(e) => setChildName(e.target.value)}
              placeholder="Enter your child's name"
              required
              className="h-11"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="childAge">Age</Label>
            <Input
              id="childAge"
              type="number"
              min="1"
              max="18"
              value={childAge}
              onChange={(e) => setChildAge(e.target.value)}
              placeholder="Enter age"
              required
              className="h-11"
            />
          </div>

          <div className="space-y-3">
            <Label>Gender</Label>
            <RadioGroup value={childGender} onValueChange={setChildGender} required className="space-y-2">
              <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 -mx-2">
                <RadioGroupItem value="boy" id="boy" className="w-5 h-5" />
                <Label htmlFor="boy" className="font-normal cursor-pointer flex-1">Boy</Label>
              </div>
              <div className="flex items-center space-x-3 p-2 rounded-lg hover:bg-muted/50 -mx-2">
                <RadioGroupItem value="girl" id="girl" className="w-5 h-5" />
                <Label htmlFor="girl" className="font-normal cursor-pointer flex-1">Girl</Label>
              </div>
            </RadioGroup>
          </div>

          <Button type="submit" className="w-full h-11" disabled={loading || detectingLocation}>
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : detectingLocation ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Setting up...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default ChildProfile;
